/**
 * @fileoverview A private page's event: what stays in the clear, what does not,
 * and the one field the ciphertext must never be put in.
 *
 * A private page is a 33301 like a public raw HTML page, because 33301 means
 * "the body is a blob" and not "private" -- settled 2026-09-15. Encryption is a
 * property this EVENT carries. That is what lets a public HTML page flip to
 * private and keep its address, so every link anyone holds keeps working.
 *
 * ---
 *
 * THE CIPHERTEXT GOES IN A TAG AND `.content` IS LEFT EMPTY. This is the one
 * decision in this file that is not inherited, it looks like a stylistic
 * preference, and it is the opposite of what the collection does one level up.
 *
 * MEASURED, and the measurement is already in `kinds.ts`. A second research pass
 * on 2026-09-15 published a 33301 with NIP-44 ciphertext INLINE in `.content`
 * and opened it in six live clients. FOUR OF THE SIX printed it as the article:
 * six hundred characters of base64 under a title, exactly as they had printed an
 * encrypted 30023. Only njump degraded honestly and only Primal showed nothing.
 * The kind number bought nothing there; what most clients do is print whatever
 * `.content` they are handed, whosever kind it arrived under.
 *
 * So the protective property is that `.content` IS EMPTY, and a tag they do not
 * know is a tag they drop on the floor. A stranger's client shows a bare event
 * rather than a wall of base64 presented as somebody's writing.
 *
 * A COLLECTION PUTS ITS SEALED HALF IN `.content` AND THAT IS STILL RIGHT.
 * Kind 33304 is not rendered by any of those clients at all, so there is no
 * screen for its content to be dumped onto. The difference is which kind a
 * stranger's reader will try to display, and 33301 is the one that shares its
 * shape with something renderable. Two files, two answers, one reason.
 *
 * ---
 *
 * WHAT STAYS IN THE CLEAR, AND IT IS EXACTLY TWO THINGS.
 *
 *   d      the page's name. It is part of the address, so it CANNOT be hidden:
 *          a reader has to be able to ask a relay for it. This is why a page's
 *          suggested name is sixteen random hex characters and never derived
 *          from the title -- see `slug.ts`. The name is the one irreversible
 *          decision in the design, and for a private page it is also the one
 *          field the key does not protect.
 *   key    the owner's envelope. Opaque, and it names only the coordinate the
 *          reader already has in their hand to be asking at all.
 *
 * Everything else -- the title, the summary, the format, the blob hash, the
 * server hints, the theme, the layout, the dates -- is inside the sealed tag.
 * The TITLE especially: a plaintext title in front of encrypted bytes is the
 * shape the operator ruled out on 2026-09-15, and it is what njump turns into an
 * `og:` preview card naming a page nobody was supposed to know about.
 *
 * ---
 *
 * THE BLOB HASH IS OF THE CIPHERTEXT, NOT OF THE BODY, and that ordering is
 * load-bearing rather than incidental. A Blossom server holds the encrypted
 * bytes and serves them to anyone who asks, per the spec. The reader verifies
 * what the SERVER GAVE IT against the hash the author signed, and only then
 * decrypts. Hashing the plaintext instead would mean decrypting first and
 * verifying second, so a hostile server's bytes would reach the crypto before
 * anything had established they were the author's bytes at all.
 *
 * ---
 *
 * WHAT THE SEALED TAG STILL SAYS, MEASURED RATHER THAN WAVED AWAY. NIP-44 pads
 * to power-of-two-ish buckets rather than to a constant, so the sealed tag's
 * length does not track the title character for character but DOES move to a
 * larger bucket for a much longer one. An observer can therefore read a coarse
 * size class of a private page's metadata -- roughly how much of it there is --
 * and never any of its content. Not fixed by padding to a fixed size on top,
 * because that is the same bucketing again with a second convention and a new
 * rule for what happens when a page exceeds it. Held as a test so it stays
 * measured; see `privatePage.test.ts`.
 *
 * THE DISCRIMINATOR IS THE ABSENT `x` TAG AND THERE IS NO `private` FLAG.
 * A plaintext `x` present means public blob-backed: fetch, verify, render.
 * Absent means sealed. A boolean would be decoration, for the same reason a
 * server-side permissions list in front of an open endpoint would be: it would
 * describe a protection rather than provide one.
 */

import type { ContentKey } from '@cloistr/auth/core';

import { OWNER_KEY_TAG } from './collectionKeys';
import type { PageFormat, PageLayout, RawEvent } from './indexEvent';
import { PAGE_INDEX_KIND } from './kinds';
import { decryptWithPageKey, encryptWithPageKey } from './pageKey';

/**
 * The tag carrying everything about a private page except its name.
 *
 * ONE TAG RATHER THAN SEVERAL ENCRYPTED ONES, because the NUMBER of sealed rows
 * would itself say something: a page with a theme and a summary would carry more
 * of them than a page with neither, so the shape of the event would describe the
 * page. One row says one thing, which is that this page is sealed, and that much
 * is unavoidable.
 */
export const SEALED_TAG = 'sealed';

/**
 * What a private page's sealed half holds.
 *
 * The field names mirror the plaintext tag names a public page uses, so the two
 * shapes can be read side by side and a field added to one is obviously missing
 * from the other.
 */
export interface SealedPage {
  title: string;
  summary: string;
  format: PageFormat;
  /** sha256 of the CIPHERTEXT blob, which is what a server actually holds. */
  x: string;
  /** Mime type of the PLAINTEXT body, not of the encrypted wrapper. */
  m: string;
  /**
   * Blossom server hints.
   *
   * Sealed like everything else, because where a page is stored is a fact about
   * the page: a hash plus a list of servers is most of an invitation to go and
   * fetch it, and a reader who cannot decrypt has no business being handed one.
   */
  servers: string[];
  theme?: string;
  toc?: boolean;
  width?: PageLayout['width'];
  columns?: 1 | 2;
  published_at?: number;
  updated_at?: number;
}

/**
 * Is this event a private page rather than a public blob-backed one?
 *
 * THE TEST IS THE ABSENT `x`, NOT THE PRESENT `sealed`, and the difference
 * matters for an event somebody else wrote. An event carrying both a plaintext
 * hash and a sealed tag is a PUBLIC page with something extra bolted on, and it
 * is read as public: the plaintext hash is what its author signed in the clear,
 * and there is nothing hidden there to protect. Reading it as private instead
 * would let one stray tag make a public page unopenable.
 */
export function isPrivatePageEvent(event: Pick<RawEvent, 'kind' | 'tags'>): boolean {
  if (event.kind !== PAGE_INDEX_KIND) return false;
  const hasHash = event.tags.some((t) => t[0] === 'x' && typeof t[1] === 'string');
  const hasSealed = event.tags.some((t) => t[0] === SEALED_TAG && typeof t[1] === 'string');
  return !hasHash && hasSealed;
}

/**
 * Build a private page's tags and content.
 *
 * `content` comes back as the empty string, ALWAYS, and it is returned rather
 * than left for the caller to omit. A caller that forgot the field would leave
 * whatever its public-page code path had put there -- the summary, most likely,
 * which is exactly the plaintext this file exists to remove.
 */
export function sealPageTags(
  identifier: string,
  sealed: SealedPage,
  pageKey: ContentKey,
  ownerKeyTag: readonly string[]
): { tags: string[][]; content: string } {
  return {
    tags: [
      ['d', identifier],
      [...ownerKeyTag],
      [SEALED_TAG, encryptWithPageKey(JSON.stringify(sealed), pageKey)],
    ],
    content: '',
  };
}

/**
 * Open a private page event into the plaintext event it would have been.
 *
 * RETURNS A `RawEvent` SO THAT `parseIndexEvent` STAYS THE ONLY PARSER. The
 * alternative -- a second function producing a `PageIndex` directly -- would be
 * two places deciding what a page's tags mean, and the day they disagree is the
 * day a private page renders with a layout its author did not choose, or skips a
 * check every public page gets. Materialising the tags and handing them to the
 * existing parser costs one object and keeps one source of truth.
 *
 * NULL ON EVERY FAILURE, NEVER A THROW. This is the read path, where a private
 * page must be indistinguishable from a missing one, and a distinct exception
 * type is precisely the thing that tells them apart. The key not opening it, the
 * payload not being a page, a truncated envelope: one answer.
 */
export function openSealedPage(event: RawEvent, pageKey: ContentKey): RawEvent | null {
  const identifier = event.tags.find((t) => t[0] === 'd' && typeof t[1] === 'string')?.[1];
  if (identifier === undefined) return null;

  const envelope = event.tags.find((t) => t[0] === SEALED_TAG && typeof t[1] === 'string')?.[1];
  if (envelope === undefined) return null;

  let sealed: SealedPage;
  try {
    const parsed: unknown = JSON.parse(decryptWithPageKey(envelope, pageKey));
    if (typeof parsed !== 'object' || parsed === null) return null;
    sealed = parsed as SealedPage;
  } catch {
    return null;
  }

  // The sealed half arrived from outside this process, inside bytes somebody
  // else signed. Its shape is a claim to check rather than one to assume: a
  // payload missing its hash or its format would otherwise reach the parser as
  // `undefined` sitting in a tag row, where it reads as a malformed event
  // rather than as the thing it is.
  if (typeof sealed.x !== 'string' || typeof sealed.format !== 'string') return null;

  const tags: string[][] = [
    ['d', identifier],
    ['x', sealed.x],
    ['format', sealed.format],
  ];
  if (typeof sealed.title === 'string') tags.push(['title', sealed.title]);
  if (typeof sealed.summary === 'string' && sealed.summary) tags.push(['summary', sealed.summary]);
  if (typeof sealed.m === 'string') tags.push(['m', sealed.m]);
  if (Array.isArray(sealed.servers)) {
    for (const server of sealed.servers) {
      if (typeof server === 'string') tags.push(['server', server]);
    }
  }
  if (typeof sealed.theme === 'string') tags.push(['theme', sealed.theme]);
  if (sealed.toc === true) tags.push(['toc', 'true']);
  if (typeof sealed.width === 'string') tags.push(['width', sealed.width]);
  if (typeof sealed.columns === 'number') tags.push(['columns', String(sealed.columns)]);
  if (typeof sealed.published_at === 'number') {
    tags.push(['published_at', String(sealed.published_at)]);
  }
  if (typeof sealed.updated_at === 'number') tags.push(['updated_at', String(sealed.updated_at)]);

  // The owner envelope is carried through deliberately. A caller that has opened
  // a page and then wants to EDIT it needs the tag it arrived on, and stripping
  // it here would mean fetching the event a second time to get it back.
  const ownerKey = event.tags.find((t) => t[0] === OWNER_KEY_TAG && typeof t[1] === 'string');
  if (ownerKey) tags.push([...ownerKey]);

  return { ...event, tags, content: '' };
}
